import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import {
  Checkbox,
  Modal,
  SkeletonLoader,
  StatusIndicator,
  ItemSelectorGroup,
  Dropdown,
  DropdownButton,
  DropdownMenu,
  DropdownMenuItem,
  Input,
} from '@unified-checkout/design-system';
import {
  Check,
  MoneyBillTransfer,
  Xmark,
} from '@unified-checkout/design-system/icons/index';
import * as amplitude from '@amplitude/analytics-browser';

import {
  BILLS_EVENTS,
  BILLS_EVENTS_PARAMS
} from '@app/dashboard/constants/tracking_events/bills';

import {
  CANCEL,
  CHECKOUT,
  EXIT,
  INQUIRY,
  PARTAIL_AMOUNT_ENTRY
} from '@app/dashboard/constants/Bills';
import { QUERY_KEYS } from '@app/dashboard/constants/query-keys';
import { getCategoryServices } from '@app/dashboard/services/inner_layout/bills/get-category-services';
import { inquiryPayment } from '@app/dashboard/services/inner_layout/bills/inquiry-payment';
import { useDynamicForm } from '@app/dashboard/views/pages/inner_layout/Bills/useDynamicForm';
import { BalancesDetails } from '@app/dashboard/components/inner_layout/BalanceDetails';
import { SummaryBox } from '@app/dashboard/components/inner_layout/Cards/SummaryBox';
import { MultiSectionCard } from '@app/dashboard/components/inner_layout/MultiSectionCard';

import type { IService, IServiceProvider } from '@app/dashboard/types/innerLayout/bills/category-services';
import type { ICategoryModalProps, TStep } from '@app/dashboard/types/innerLayout/bills/category-modal';

import { DynamicField } from './DynamicField';
import styles from './category-modal.module.scss';

const STEP = {
  FORM: 1,
  DETAILS: 2,
  RESULT: 3,
  SMS: 4,
  PARTIAL: 5,
} as const;

export const CategoryModal = ({
  category,
  billsData,
  result,
  onClose,
  onSubmit,
  onConfirmSms,
  isSubmitting,
  isConfirmingSms,
}: ICategoryModalProps) => {
  const { t } = useTranslation();
  const [step, setStep] = useState<TStep>(STEP.FORM);
  const [selectedProvider, setSelectedProvider] = useState<IServiceProvider | null>(null);
  const [selectedService, setSelectedService] = useState<IService | null>(null);
  const [saveBill, setSaveBill] = useState({
    name: '',
    checked: false,
  });
  const [partialAmount, setPartialAmount] = useState('');
  const [inquiryData, setInquiryData] = useState<any>(null);
  const [inquiryValidationError, setInquiryValidationError] = useState('');

  const { data, isPending } = useQuery({
    queryKey: [QUERY_KEYS.GET_CATEGORY_SERVICES, category?.category_id],
    queryFn: () => getCategoryServices(category!.category_id),
    enabled: !!category,
  });

  const providers: IServiceProvider[] = data?.data?.data ?? [];
  const activeProvider = selectedProvider ?? providers[0] ?? null;
  const activeService = selectedService ?? activeProvider?.services[0] ?? null;
  const fields = activeService?.fields_required ?? [];

  const { values, errors, handleChange, isValid, getPayload, resetForm } = useDynamicForm(fields);

  const { mutate: runInquiry, isPending: isInquiryPending, error: inquiryError, reset: resetInquiry } = useMutation({
    mutationFn: inquiryPayment,
    onSuccess: (res) => {
      const responseData = res?.data?.data;

      if (responseData?.bill_details === null && responseData?.due_bills === null) {
        setInquiryData(null);
        setInquiryValidationError(res?.data.message);
        return;
      }

      setInquiryValidationError('');
      setInquiryData(responseData);
      setStep(STEP.DETAILS);
    },
    onError: (error: any) => {
      const errData = error?.response?.data;
      const title = errData?.status ?? t('innerLayout.billsPage.allServices.inquiryFailedTitle');
      const description = errData?.message ?? t('innerLayout.billsPage.allServices.unexpectedResponseFormat');
      // setInquiryData(null);
      // onSubmit({ _error: true, title, description }, 0);
    },
  });
  const errData = (inquiryError as any)?.response?.data;

  const handleProviderSelect = (provider: IServiceProvider) => {
    setSelectedProvider(provider);
    setSelectedService(null);
    setInquiryValidationError('');
    resetInquiry();
  };

  const handleServiceSelect = (service: IService) => {
    setSelectedService(service);
    resetForm();
    setInquiryValidationError('');
    resetInquiry();
  };

  const handleClose = () => {
    amplitude.track(BILLS_EVENTS.BILLS_SCREEN_EXITED, {
      [BILLS_EVENTS_PARAMS.SCREEN_NAME]: category?.category_title,
      [BILLS_EVENTS_PARAMS.FLOW_STEP]: step === STEP.FORM ? INQUIRY : step === STEP.DETAILS ? CHECKOUT : step === STEP.PARTIAL ? PARTAIL_AMOUNT_ENTRY : '',
      [BILLS_EVENTS_PARAMS.EXIT_TYPE]: step === STEP.FORM ? EXIT : CANCEL,
    });
    setStep(STEP.FORM);
    setSelectedProvider(null);
    setSelectedService(null);
    setSaveBill({ name: '', checked: false });
    setInquiryData(null);
    setPartialAmount('');
    setInquiryValidationError('');
    resetInquiry();
    onClose();
  };

  const handleContinue = () => {
    if (!activeService) return;

    if (result?.instructions && onConfirmSms) {
      onConfirmSms(result?.statusCheck ?? '');
      return;
    }

    amplitude.track(BILLS_EVENTS.BILLS_CONTINUE_SELECTED, {
      [BILLS_EVENTS_PARAMS.BILL_CATEGORY]: category?.category_title,
      [BILLS_EVENTS_PARAMS.BILL_PROVIDER]: activeProvider?.service_provider,
      [BILLS_EVENTS_PARAMS.BILL_SERVICE]: activeService.service_title,
      [BILLS_EVENTS_PARAMS.REMAINING_BILLS_BALANCE]: billsData?.data?.data?.balance?.amount - inquiryData?.bill_details?.amount,
      [BILLS_EVENTS_PARAMS.STATUS]: data?.data?.status,
      [BILLS_EVENTS_PARAMS.ERROR]: (inquiryError as any)?.response?.data?.message,
    });

    setInquiryValidationError('');
    runInquiry({
      category_id: Number(category?.category_id),
      service_id: activeService.service_id,
      required_data: getPayload(),
      top_up: category?.category_title?.toLowerCase().includes('cash'),
    });
  };

  const handlePayNow = () => {
    if (!activeService) return;
    // onSubmit(getPayload(), activeService.service_id, saveBill);
    const payload = getPayload();
    // include bill_id, fees and amount from inquiry when available
    const billId = inquiryData?.bill_details?.bill_id ?? payload.bill_id ?? [];
    const fees = inquiryData?.bill_details?.fees ?? payload.fees ?? 0;
    const amount = step === STEP.PARTIAL ? partialAmount : (inquiryData?.bill_details?.amount ?? payload.amount);

    if (step === STEP.PARTIAL) {
      amplitude.track(BILLS_EVENTS.BILLS_PARTIAL_PAYMENT_CONFIRMED, {
        [BILLS_EVENTS_PARAMS.BILL_CATEGORY]: category?.category_title,
        [BILLS_EVENTS_PARAMS.BILL_PROVIDER]: activeProvider?.service_provider,
        [BILLS_EVENTS_PARAMS.BILL_SERVICE]: activeService.service_title,
        [BILLS_EVENTS_PARAMS.TOTAL_AMOUNT]: inquiryData?.bill_details?.amount,
        [BILLS_EVENTS_PARAMS.PARTIAL_AMOUNT]: amount,
        [BILLS_EVENTS_PARAMS.STATUS]: data?.data?.status,
        [BILLS_EVENTS_PARAMS.ERROR]: (inquiryError as any)?.response?.data?.message,
        [BILLS_EVENTS_PARAMS.TRANSACTION_ID]: inquiryData?.transaction_id,
      });
    } else {
      amplitude.track(BILLS_EVENTS.BILLS_PAY_SELECTED, {
        [BILLS_EVENTS_PARAMS.BILL_CATEGORY]: category?.category_title,
        [BILLS_EVENTS_PARAMS.BILL_PROVIDER]: activeProvider?.service_provider,
        [BILLS_EVENTS_PARAMS.BILL_SERVICE]: activeService.service_title,
        [BILLS_EVENTS_PARAMS.TOTAL_AMOUNT]: amount,
        [BILLS_EVENTS_PARAMS.REMAINING_BILLS_BALANCE]: billsData?.data?.data?.balance?.amount - inquiryData?.bill_details?.amount,
        [BILLS_EVENTS_PARAMS.STATUS]: data?.data?.status,
        [BILLS_EVENTS_PARAMS.ERROR]: (inquiryError as any)?.response?.data?.message,
        [BILLS_EVENTS_PARAMS.SAVE_BILL]: saveBill.checked,
      });
    }

    onSubmit({
      ...payload,
      bill_id: billId,
      fees,
      amount,
      save_bill: saveBill.checked,
      bill_name: saveBill.name
    },
      activeService.service_id,
    );
  };

  const handlePayPartially = () => {
    amplitude.track(BILLS_EVENTS.BILLS_PARTIAL_PAYMENT_SELECTED, {
      [BILLS_EVENTS_PARAMS.BILL_CATEGORY]: category?.category_title,
      [BILLS_EVENTS_PARAMS.BILL_PROVIDER]: activeProvider?.service_provider,
      [BILLS_EVENTS_PARAMS.BILL_SERVICE]: activeService?.service_title,
      [BILLS_EVENTS_PARAMS.TOTAL_AMOUNT]: inquiryData?.bill_details?.amount,
      [BILLS_EVENTS_PARAMS.TRANSACTION_ID]: inquiryData?.transaction_id,
      [BILLS_EVENTS_PARAMS.STATUS]: data?.data?.status,
      [BILLS_EVENTS_PARAMS.ERROR]: (inquiryError as any)?.response?.data?.message,
    });
    setStep(STEP.PARTIAL);
  }

  useEffect(() => {
    if (category) {
      if (!isPending) {
        amplitude.track(BILLS_EVENTS.BILLS_SERVICE_CATEGORY_PAGE_VIEWED, {
          [BILLS_EVENTS_PARAMS.BILL_CATEGORY]: category.category_title,
          [BILLS_EVENTS_PARAMS.STATUS]: data?.data?.status,
        });
      }
    }
  }, [category, isPending]);

  useEffect(() => {
    if (step === STEP.DETAILS) {
      amplitude.track(BILLS_EVENTS.BILLS_CHECKOUT_PAGE_VIEWED, {
        [BILLS_EVENTS_PARAMS.BILL_CATEGORY]: category?.category_title,
        [BILLS_EVENTS_PARAMS.BILL_PROVIDER]: activeProvider?.service_provider,
        [BILLS_EVENTS_PARAMS.BILL_SERVICE]: activeService?.service_title,
        [BILLS_EVENTS_PARAMS.TOTAL_AMOUNT]: inquiryData?.bill_details?.amount,
        [BILLS_EVENTS_PARAMS.STATUS]: inquiryData?.data?.status,
      });
    }
    if (step === STEP.PARTIAL) {
      amplitude.track(BILLS_EVENTS.BILLS_PARTIAL_PAYMENT_PAGE_VIEWED, {
        [BILLS_EVENTS_PARAMS.BILL_CATEGORY]: category?.category_title,
        [BILLS_EVENTS_PARAMS.BILL_PROVIDER]: activeProvider?.service_provider,
        [BILLS_EVENTS_PARAMS.BILL_SERVICE]: activeService?.service_title,
        [BILLS_EVENTS_PARAMS.TOTAL_AMOUNT]: inquiryData?.bill_details?.amount,
      });
    }
  }, [step]);

  const renderHeader = () => {
    if (step === STEP.PARTIAL) {
      return (
        <div className={styles['category-modal__header']}>
          <span className={styles['category-modal__header__title']}>
            {t('innerLayout.billsPage.allServices.enterNewValue')}
          </span>
          <span className={styles['category-modal__header__description']}>
            {t('innerLayout.billsPage.allServices.enterAmountToPay')}
          </span>
        </div>
      );
    }

    return (
      <div className={styles['category-modal__header']}>
        {
          category?.category_icon ? 
          <img
            className={styles['category-modal__header__icon']}
            src={category?.category_icon}
            alt={category?.category_title}
          />
          :
          <div className={styles['category-modal__header__icon']}>
            {MoneyBillTransfer}
          </div>
        }
        <span className={styles['category-modal__header__title']}>
          {category?.category_title}
        </span>
      </div>
    );
  };

  const renderProviderTabs = () => {
    if (isPending) return <SkeletonLoader type='transparent_box' noOfRows={1} height='2rem' />;
    if (!providers?.length) return null;

    return (
      <Dropdown label={t('innerLayout.billsPage.allServices.company')}>
        <DropdownButton
          type='button'
          leadingIcon={activeProvider?.service_icon ? <img src={activeProvider.service_icon} width="24px" alt={activeProvider.service_provider} /> : undefined}
          text={activeProvider?.service_provider || t('innerLayout.billsPage.allServices.selectProvider')}
          placeholder={t('innerLayout.billsPage.allServices.selectProvider')}
          disabled={isPending || isInquiryPending}
        />
        <DropdownMenu>
          {providers.map((provider) => (
            <DropdownMenuItem
              key={provider.category_id}
              type='default'
              text={provider.service_provider}
              icon={provider.service_icon ? <img src={provider.service_icon} alt={provider.service_provider} /> : undefined}
              onClickHandler={() => handleProviderSelect(provider)}
            />
          ))}
        </DropdownMenu>
      </Dropdown>
    );
  };

  const renderServiceTabs = () => {
    if (!activeProvider?.services?.length) return null;

    const serviceItems = activeProvider.services.map((service) => ({
      id: service.service_id.toString(),
      text: service.service_title,
      hintText: '',
      selected: activeService?.service_id === service.service_id,
      onClickHandler: () => handleServiceSelect(service),
      disabled: isPending || isInquiryPending,
      badgeProps: undefined,
    }));

    return (
      <div className={styles['category-modal__services']}>
        <ItemSelectorGroup
          type='radio'
          label={t('innerLayout.billsPage.allServices.service')}
          orientation='horizontal'
          hasFirstItemSelected={false}
          items={serviceItems}
        />
      </div>
    );
  };

  const renderSavedBillCheckbox = () => {
    if (!inquiryData?.payment_details?.display_saved_bills) return;

    return (
      <>
        <Checkbox
          type='checkbox'
          id='save-bill'
          text={t('innerLayout.billsPage.allServices.saveBillCheckbox')}
          checked={saveBill.checked}
          onClickHandler={(checked) => {
            if (checked) {
              amplitude.track(BILLS_EVENTS.BILLS_SAVE_BILL_SELECTED, {
                [BILLS_EVENTS_PARAMS.BILL_CATEGORY]: category?.category_title,
                [BILLS_EVENTS_PARAMS.BILL_PROVIDER]: activeProvider?.service_provider,
                [BILLS_EVENTS_PARAMS.BILL_SERVICE]: activeService?.service_title,
                [BILLS_EVENTS_PARAMS.BILL_AMOUNT]: step === STEP.PARTIAL ? partialAmount : inquiryData?.bill_details?.amount,
              });
            }
            setSaveBill({ ...saveBill, checked });
          }}
        />
        {
          saveBill.checked &&
          <Input
            name='bill-name'
            type='text'
            label={t('innerLayout.billsPage.allServices.saveBillNameInputLabel')}
            placeholder={t('innerLayout.billsPage.allServices.saveBillNameInputPlaceholder')}
            value={saveBill.name}
            onChange={(e) => setSaveBill({ ...saveBill, name: e.target.value })}
          />
        }
      </>
    )
  }
  const renderCashback = () => {
    return (
      inquiryData?.payment_details?.cashback_data?.title && (
        <div className={styles['category-modal__body__note']}>
          <div
            className={styles['category-modal__body__note__title']}
            style={{ color: inquiryData.payment_details.cashback_data.title_color }}
          >
            <img src={inquiryData.payment_details.cashback_data.icon} alt="icon" />
            {inquiryData.payment_details.cashback_data.title}
          </div>
          <div className={styles['category-modal__body__note__description']}>
            {inquiryData.payment_details.cashback_data.sub_title}
          </div>
        </div>
      )
    )
  }

  const renderPartialPayment = () => {
    const totalPartialAmount = Number(inquiryData?.bill_details?.amount) + Number(inquiryData?.bill_details?.fees);
    const isPartialValid = Number(partialAmount) >= 0 && Number(partialAmount) <= totalPartialAmount;

    return (
      <div className={styles['category-modal__body__partial-payment']}>
        <Input
          name='partial-payment'
          type='number'
          label={t('innerLayout.billsPage.allServices.partialPaymentInputLabel')}
          placeholder={"0.00"}
          value={partialAmount}
          status={!isPartialValid ? 'error' : undefined}
          error={Number(partialAmount) > totalPartialAmount ? t('innerLayout.billsPage.allServices.partialPaymentError') : ""}
          onChange={(e) => setPartialAmount(e.target.value)}
        />
        <div className={styles['category-modal__body__partial-payment__balance']}>
          <div className={styles['category-modal__body__partial-payment__balance__label']}>
            {t('innerLayout.billsPage.allServices.totalBillAmount')}
          </div>
          <div className={styles['category-modal__body__partial-payment__balance__value']}>
            {totalPartialAmount} {inquiryData?.payment_details?.balance?.currency}
          </div>
        </div>
      </div>
    );
  }

  const renderFields = () => {
    if (isPending) return (
      <>
        <SkeletonLoader type='transparent_box' noOfRows={1} height='2rem' width='30%' />
        <SkeletonLoader type='transparent_box' noOfRows={1} height='2rem' />
        <SkeletonLoader type='transparent_box' noOfRows={1} height='2rem' width='30%' />
        <SkeletonLoader type='transparent_box' noOfRows={1} height='2rem' />
      </>
    );

    return fields.map((field) => (
      <DynamicField
        key={field.field_name}
        field={field}
        value={values[field.field_name]}
        error={errors[field.field_name]}
        onChange={handleChange}
        disabled={isPending || isInquiryPending}
      />
    ));
  };

  const renderDetailsStep = () => {
    const detailPairs = (inquiryData?.payment_details?.details ?? []).map((item: any) => ({
      label: item.title,
      value: item.value,
    }));

    return (
      <>
        <SummaryBox
          title={t('innerLayout.billsPage.allServices.detailsTitle')}
          itemsList={detailPairs}
          width="100%"
        />
        {renderCashback()}
        {renderSavedBillCheckbox()}
      </>
    );
  };

  const renderContent = () => {
    if (result) {
      if (result.instructions) {
        return (
          <>
            {result.instructions.map((step, index) => (
              <MultiSectionCard
                key={index}
                title={step.title}
                subtitle={step.description}
                icon={step.icon}
              />
            ))}
          </>
        );
      }

      return (
        <StatusIndicator
          status={result.status === 'success' ? 'success' : 'error'}
          icon={result.status === 'success' ? Check : Xmark}
          text={result.title}
          hintText={result.description}
        />
      );
    }

    if (step === STEP.PARTIAL) {
      return renderPartialPayment();
    }

    return (
      <>
        <BalancesDetails
          title={billsData?.data?.data?.balance?.title ?? t('innerLayout.billsPage.billsBalance')}
          amount={billsData?.data?.data?.balance?.amount}
          currency={billsData?.data?.data?.balance?.currency}
        />
        {step === STEP.DETAILS ? (
          renderDetailsStep()
        ) : (
          <>
            {renderProviderTabs()}
            {renderServiceTabs()}
            {renderFields()}
          </>
        )}
      </>
    );
  };

  const getBtnsProps = () => {
    const hasInstructions = !!result?.instructions;
    const payPartial = inquiryData?.payment_details?.allow_partial;
    const isLoading = isInquiryPending || isConfirmingSms;
    const isFormDisabled = !hasInstructions && (!isValid || isPending || isLoading);

    if (result && !hasInstructions) return undefined;

    const isFormStep = step === STEP.FORM || hasInstructions;
    const isPartialStep = step === STEP.PARTIAL;
    const isDefaultStep = !isFormStep && !isPartialStep;
    const totalPartialAmount = Number(inquiryData?.bill_details?.amount) + Number(inquiryData?.bill_details?.fees);
    const isPartialValueDisable = !partialAmount || Number(partialAmount) > totalPartialAmount || Number(partialAmount) <= 0;

    const buttons = [
      {
        show: isFormStep,
        text: t('innerLayout.billsPage.allServices.continueButton'),
        onClickHandler: handleContinue,
        disabled: isFormDisabled,
        loading: isLoading,
      },
      {
        show: isFormStep,
        color: 'secondary' as const,
        variant: 'outlined' as const,
        text: t('innerLayout.billsPage.allServices.cancelButton'),
        onClickHandler: handleClose,
      },
      {
        show: isPartialStep,
        text: t('form.confirm'),
        onClickHandler: handlePayNow,
        disabled: isSubmitting || isPartialValueDisable,
        loading: isSubmitting,
      },
      {
        show: isPartialStep,
        color: 'secondary' as const,
        variant: 'outlined' as const,
        text: t('form.back'),
        onClickHandler: () => {
          amplitude.track(BILLS_EVENTS.BILLS_BACK_SELECTED, {
            [BILLS_EVENTS_PARAMS.SCREEN_NAME]: category?.category_title,
            [BILLS_EVENTS_PARAMS.FLOW_STEP]: PARTAIL_AMOUNT_ENTRY,
          });
          setStep(STEP.DETAILS);
        },
        disabled: isSubmitting,
      },
      {
        show: isDefaultStep,
        text: t('innerLayout.billsPage.allServices.payNowButton'),
        onClickHandler: handlePayNow,
        disabled: isSubmitting,
        loading: isSubmitting,
      },
      {
        show: isDefaultStep && payPartial,
        text: t('innerLayout.billsPage.allServices.payPartiallyButton'),
        onClickHandler: handlePayPartially,
        loading: isLoading,
        variant: 'link' as const,
      },
      {
        show: isDefaultStep && !payPartial,
        color: 'secondary' as const,
        variant: 'outlined' as const,
        text: t('innerLayout.billsPage.allServices.cancelButton'),
        onClickHandler: () => {
          amplitude.track(BILLS_EVENTS.BILLS_BACK_SELECTED, {
            [BILLS_EVENTS_PARAMS.SCREEN_NAME]: category?.category_title,
            [BILLS_EVENTS_PARAMS.FLOW_STEP]: CHECKOUT,
          });
          setStep(STEP.FORM);
        },
        disabled: isSubmitting,
      },
    ];

    return buttons.filter(b => b.show).map(({ show, ...btn }) => btn);
  };

  return (
    <Modal
      open={!!category}
      hasCloseIcon
      closeOnClickOutside={false}
      onCloseHandler={handleClose}
      title={result?.status === 'sms' ? result?.title : result ? undefined : renderHeader() as any}
      btnsProps={getBtnsProps()}
      errorMsg={errData?.message || inquiryValidationError || ''}
      position='center'
    >
      <div className={styles['category-modal__body']}>
        {renderContent()}
      </div>
    </Modal>
  );
};
